import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, Link2, RefreshCw, Share2, Copy, CheckCircle, X, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentLinkButton = ({ 
  referenceType,
  referenceId,
  amount,
  customerName,
  customerPhone,
  customerEmail,
  title,
  existingLinks = [],
  onLinkCreated,
  onPaymentConfirmed,
  variant = 'default',
  disabled = false,
  compact = false,
  asDropdownItem = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [linkAmount, setLinkAmount] = useState(amount || 0);
  const [linkTitle, setLinkTitle] = useState(title || `Payment for Order`);
  const [createdLink, setCreatedLink] = useState(null);

  // Keep the link amount in sync when the due amount changes
  // (e.g., driver selects rejected items for a partial delivery)
  useEffect(() => {
    if (!createdLink?.url) setLinkAmount(amount || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);
  const [isPaid, setIsPaid] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [fetchedLinks, setFetchedLinks] = useState([]);
  const [forceNew, setForceNew] = useState(false);

  // On open, load previously-created links from the server so a link
  // survives closing the popup — user can re-share / re-check status.
  useEffect(() => {
    if (!isOpen || !referenceId) return;
    setForceNew(false);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_BASE_URL}/mamopay/links/${referenceType}/${referenceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFetchedLinks(r.data?.payment_links || []);
      } catch { /* noop */ }
    })();
  }, [isOpen, referenceType, referenceId]);
  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  // Stash the freshly-created link inside a ref so the polling
  // setInterval closure (which captured an empty `createdLink`) can
  // still see the Stripe session id for gateway-aware status checks.
  const activeLinkRef = useRef(null);

  // Customer name parts
  const nameParts = (customerName || '').trim().split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Reset when dialog closes
  const handleClose = () => {
    setIsOpen(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // Reset states after a short delay
    setTimeout(() => {
      setCreatedLink(null);
      setIsPaid(false);
      setShowQR(false);
      pollCountRef.current = 0;
      activeLinkRef.current = null;
    }, 300);
  };

  // Check payment status. Branches by gateway:
  //  * `stripe` — call `/payment/stripe/verify-session?session_id=...`
  //    which re-fetches the session from Stripe and marks paid
  //    server-side. Public endpoint (no auth needed for the
  //    customer's success page, but called here with auth — still
  //    works). Closes the user-reported "driver creates Stripe link,
  //    customer pays, status doesn't update" bug.
  //  * `mamopay` — call `/mamopay/link/{id}/status` as before.
  //  * Either gateway: also try the local-DB fallback in case the
  //    webhook already wrote `is_paid: true` to the embedded link
  //    entry.
  const checkStatus = useCallback(async (linkId, linkOverride = null) => {
    if (!linkId) return false;
    const link = linkOverride || activeLinkRef.current || createdLink || {};
    const gateway = link.gateway;
    const stripeSession = link.stripe_session_id;

    try {
      const token = localStorage.getItem('token');
      let res;
      if (gateway === 'stripe' && stripeSession) {
        res = await axios.get(
          `${API_BASE_URL}/payment/stripe/verify-session?session_id=${encodeURIComponent(stripeSession)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        res = await axios.get(`${API_BASE_URL}/mamopay/link/${linkId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (res.data?.is_paid || res.data?.paid) {
        setIsPaid(true);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        toast.success('Payment Received!', { duration: 8000 });
        if (onPaymentConfirmed) onPaymentConfirmed();
        return true;
      }
    } catch (err) {
      // Either gateway: fall back to a local-DB scan in case the
      // webhook beat us to it.
      try {
        const token = localStorage.getItem('token');
        const ref = await axios.get(`${API_BASE_URL}/mamopay/links/${referenceType}/${referenceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const links = ref.data?.payment_links || [];
        const paidLink = links.find(l => l.is_paid);
        if (paidLink) {
          setIsPaid(true);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          toast.success('Payment Received!', { duration: 8000 });
          if (onPaymentConfirmed) onPaymentConfirmed();
          return true;
        }
      } catch (e) {
        console.error('Status check failed:', e);
      }
    }
    return false;
  }, [onPaymentConfirmed, referenceType, referenceId, createdLink]);

  // Start polling. Accepts an optional `linkOverride` so callers that
  // just created a link can pass it directly — avoiding a stale
  // closure where `createdLink` is still null on the first tick.
  const startPolling = useCallback((linkId, linkOverride = null) => {
    if (!linkId || pollingRef.current) return;
    if (linkOverride) activeLinkRef.current = linkOverride;

    pollCountRef.current = 0;
    pollingRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      const paid = await checkStatus(linkId, activeLinkRef.current);

      // Stop after 60 checks (10 minutes at 10s interval)
      if (paid || pollCountRef.current >= 60) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }, 10000);
  }, [checkStatus]);

  // Create payment link
  const handleCreate = async () => {
    if (!linkAmount || linkAmount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      // Use gateway-aware endpoint that routes to Stripe or MamoPay based on company settings
      const res = await axios.post(`${API_BASE_URL}/payment/create-link`, {
        amount: parseFloat(linkAmount),
        title: linkTitle,
        description: `${linkTitle} - ${customerName || 'Customer'}`,
        customer_first_name: firstName,
        customer_last_name: lastName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        reference_type: referenceType,
        reference_id: referenceId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.success) {
        const newLink = {
          url: res.data.payment_url,
          id: res.data.payment_link_id,
          amount: res.data.amount || linkAmount,
          gateway: res.data.gateway || 'mamopay',
          stripe_session_id: res.data.stripe_session_id || null,
        };
        setCreatedLink(newLink);
        toast.success('Payment link created!');
        if (onLinkCreated) onLinkCreated(res.data);

        // Start polling for BOTH gateways. Stripe now has a working
        // verify-session endpoint, so polling closes the loop even
        // when the merchant hasn't set up the Stripe webhook yet.
        if (res.data.payment_link_id) {
          startPolling(res.data.payment_link_id, newLink);
        }
      } else {
        toast.error('Failed to create link');
      }
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setCreating(false);
    }
  };

  // Manual status check
  const handleCheckStatus = async (linkId) => {
    setChecking(true);
    const paid = await checkStatus(linkId);
    if (!paid) {
      toast.info('Payment not yet received');
    }
    setChecking(false);
  };

  // Copy link
  const handleCopy = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  // Share link
  const handleShare = async (url) => {
    if (!url) return;
    
    // Start polling when sharing
    if (createdLink?.id) {
      startPolling(createdLink.id);
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: linkTitle,
          text: `Please pay: ${linkTitle}`,
          url: url
        });
        toast.success('Shared! Waiting for payment...');
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopy(url);
        }
      }
    } else {
      handleCopy(url);
    }
  };

  // Find existing unpaid link
  const activeLink = [...(existingLinks || []), ...fetchedLinks].find(l => !l.is_paid && l.status === 'active');

  // Render modal content
  const renderContent = () => {
    // Payment confirmed view
    if (isPaid) {
      return (
        <div className="p-6 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-green-400 mb-2">Payment Received!</h3>
          <p className="text-gray-300 mb-4">Customer has paid successfully</p>
          <p className="text-green-400 font-bold text-2xl mb-6">
            AED {createdLink?.amount || linkAmount}
          </p>
          <Button onClick={handleClose} className="bg-green-500 hover:bg-green-600 text-white w-full">
            Done
          </Button>
        </div>
      );
    }

    // Link created - show share options
    if (createdLink?.url) {
      return (
        <div className="p-4 space-y-4">
          <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">Link Created!</span>
            </div>
            <p className="text-white font-bold">AED {createdLink.amount}</p>
          </div>

          {pollingRef.current && (
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
              <span className="text-blue-300 text-sm">Waiting for payment...</span>
            </div>
          )}

          {showQR && (
            <div
              className="bg-white rounded-lg p-4 flex flex-col items-center gap-2"
              data-testid="payment-link-qr-container"
            >
              <QRCodeSVG
                value={createdLink.url}
                size={200}
                level="M"
                includeMargin={false}
                data-testid="payment-link-qr-code"
              />
              <p className="text-gray-700 text-xs text-center">
                Customer can scan this with their phone camera to pay
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Button onClick={() => handleShare(createdLink.url)} className="bg-blue-500 hover:bg-blue-600 text-white w-full" data-testid="share-payment-link-btn">
              <Share2 className="w-4 h-4 mr-2" />
              Share with Customer
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowQR((v) => !v)}
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 w-full"
              data-testid="toggle-payment-link-qr-btn"
            >
              <QrCode className="w-4 h-4 mr-2" />
              {showQR ? 'Hide QR Code' : 'Show QR Code'}
            </Button>

            <Button variant="outline" onClick={() => handleCopy(createdLink.url)} className="border-gray-600 text-gray-200 w-full" data-testid="copy-payment-link-btn">
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>

            <Button 
              variant="outline" 
              onClick={() => handleCheckStatus(createdLink.id)}
              disabled={checking}
              className="border-green-500/50 text-green-400 hover:bg-green-500/10 w-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Checking...' : 'Check Payment Status'}
            </Button>
          </div>

          <p className="text-gray-500 text-xs text-center">
            Auto-checks every 10 seconds after sharing
          </p>
        </div>
      );
    }

    // Existing link available
    if (!forceNew && activeLink?.payment_url) {
      return (
        <div className="p-4 space-y-4">
          <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
            <p className="text-yellow-400 text-sm mb-1">Existing link:</p>
            <p className="text-white font-bold">AED {activeLink.amount}</p>
          </div>

          {showQR && (
            <div
              className="bg-white rounded-lg p-4 flex flex-col items-center gap-2"
              data-testid="payment-link-qr-container"
            >
              <QRCodeSVG
                value={activeLink.payment_url}
                size={200}
                level="M"
                includeMargin={false}
                data-testid="payment-link-qr-code"
              />
              <p className="text-gray-700 text-xs text-center">
                Customer can scan this with their phone camera to pay
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              onClick={() => {
                if (activeLink.mamopay_link_id) startPolling(activeLink.mamopay_link_id);
                handleShare(activeLink.payment_url);
              }} 
              className="bg-blue-500 hover:bg-blue-600 text-white w-full"
              data-testid="existing-share-link-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Link
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowQR((v) => !v)}
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 w-full"
              data-testid="existing-toggle-qr-btn"
            >
              <QrCode className="w-4 h-4 mr-2" />
              {showQR ? 'Hide QR Code' : 'Show QR Code'}
            </Button>

            <Button
              variant="outline"
              onClick={() => handleCopy(activeLink.payment_url)}
              className="border-gray-600 text-gray-200 w-full"
              data-testid="existing-copy-link-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>

            <Button 
              variant="outline" 
              onClick={() => handleCheckStatus(activeLink.mamopay_link_id)}
              disabled={checking}
              className="border-green-500/50 text-green-400 w-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
              Check Status
            </Button>

            <Button 
              variant="outline" 
              onClick={() => { setForceNew(true); setCreatedLink(null); }}
              className="border-gray-600 text-gray-400 w-full"
            >
              Create New Link
            </Button>
          </div>
        </div>
      );
    }

    // Create new link form
    return (
      <div className="p-4 space-y-4">
        <div>
          <Label className="text-gray-200">Amount (AED)</Label>
          <Input
            type="number"
            min="1"
            value={linkAmount}
            onChange={(e) => setLinkAmount(e.target.value)}
            className="bg-gray-800 border-gray-600 text-white mt-1"
            placeholder="Enter amount"
          />
        </div>

        <div>
          <Label className="text-gray-200">Description</Label>
          <Input
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            className="bg-gray-800 border-gray-600 text-white mt-1"
          />
        </div>

        {customerName && (
          <div className="text-sm text-gray-400">
            <p>Customer: {customerName}</p>
            {customerPhone && <p>Phone: {customerPhone}</p>}
          </div>
        )}

        <Button onClick={handleCreate} disabled={creating} className="bg-blue-500 hover:bg-blue-600 text-white w-full">
          {creating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4 mr-2" />
              Create Payment Link
            </>
          )}
        </Button>
      </div>
    );
  };

  return (
    <>
      {asDropdownItem ? (
        <button
          onClick={() => setIsOpen(true)}
          disabled={disabled}
          className="flex w-full items-center px-2 py-1.5 text-sm text-purple-400 hover:bg-purple-500/10 rounded-sm cursor-pointer"
          data-testid="create-payment-link-dropdown"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Payment Link
        </button>
      ) : (
        <Button
          onClick={() => setIsOpen(true)}
          disabled={disabled}
          className={variant === 'driver' ? 'bg-blue-500 hover:bg-blue-600 text-white w-full' : 'bg-blue-500 hover:bg-blue-600 text-white'}
          size={variant === 'default' ? 'sm' : 'default'}
          data-testid="create-payment-link-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {variant === 'driver' ? 'Create Payment Link' : 'Payment Link'}
        </Button>
      )}

      {/* Custom Modal — portaled to <body> so it renders as a proper
          centered dialog even when the trigger sits inside a transformed
          dropdown menu (fixed positioning breaks inside transforms). */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={handleClose}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70" />
          
          {/* Modal */}
          <div 
            className="relative bg-gray-900 border border-gray-700 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-white" data-testid="payment-link-modal-title">
                  {createdLink?.gateway === 'stripe'
                    ? 'Stripe Payment'
                    : createdLink?.gateway === 'mamopay'
                      ? 'MamoPay Payment'
                      : 'Payment Link'}
                </h2>
              </div>
              <button 
                onClick={handleClose}
                className="text-gray-400 hover:text-white p-1 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            {renderContent()}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PaymentLinkButton;
